import React from "react";
import { Link } from "react-router-dom";
import MarkdownContent from "@/components/MarkdownContent";
import {
  GraduationCap, Radio, ShieldCheck, Cpu, Award, Users, Building2,
  MessageSquare, Star, Sparkles, Compass, CreditCard, ArrowRight, CheckCircle2,
  BookOpen, Play,
} from "lucide-react";

const ICON_MAP = {
  GraduationCap, Radio, ShieldCheck, Cpu, Award, Users, Building2,
  MessageSquare, Star, Sparkles, Compass, CreditCard, BookOpen, CheckCircle2,
};

function ResolvedIcon({ name, className }) {
  const C = ICON_MAP[name] || Sparkles;
  return <C className={className} />;
}

/**
 * Fase 13.3 · Render de una landing partner desde su JSON blocks.
 * Recibe `landing` (con blocks, tenant_branding, branding_override, tenant_name)
 * y `programs` (opcional · map por slug para `programs_showcase`).
 * Aplica CSS vars `--partner-primary`, `--partner-secondary`, `--partner-font`.
 */
export default function PartnerLandingRenderer({ landing, programs = {}, editorMode = false, onSelectBlock, selectedBlockId }) {
  const branding = { ...(landing.tenant_branding || {}), ...(landing.branding_override || {}) };
  const primary = branding.primary_color || "#DC5A0F";
  const secondary = branding.secondary_color || "#F59E0B";
  const font = branding.font_family || "";

  const styleVars = {
    "--partner-primary": primary,
    "--partner-secondary": secondary,
    "--partner-font": font || "inherit",
    fontFamily: font || undefined,
  };

  const visible = (landing.blocks || []).filter((b) => b.is_visible !== false);
  const list = editorMode ? (landing.blocks || []) : visible;

  return (
    <div data-testid="partner-landing-renderer" style={styleVars} className="bg-white text-slate-900">
      {list.length === 0 && !editorMode && (
        <div className="max-w-2xl mx-auto py-24 px-4 text-center">
          <p className="text-slate-500">Esta landing aún no tiene contenido.</p>
        </div>
      )}
      {list.map((b) => (
        <BlockWrapper
          key={b.id}
          block={b}
          editorMode={editorMode}
          selected={selectedBlockId === b.id}
          onClick={editorMode ? () => onSelectBlock?.(b.id) : undefined}
        >
          <Block block={b} landing={landing} programs={programs} primary={primary} secondary={secondary} />
        </BlockWrapper>
      ))}
    </div>
  );
}

function BlockWrapper({ block, editorMode, selected, onClick, children }) {
  if (!editorMode) return children;
  return (
    <div
      onClick={onClick}
      data-testid={`editor-block-${block.type}-${block.id}`}
      className={`relative cursor-pointer transition-all ${
        selected ? "ring-4 ring-[#DC5A0F] ring-inset" : "hover:ring-2 hover:ring-[#93C5FD] hover:ring-inset"
      } ${block.is_visible === false ? "opacity-40" : ""}`}
    >
      <div className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/95 border border-slate-200 text-[10px] font-bold uppercase tracking-widest text-slate-600 pointer-events-none">
        {block.type}
      </div>
      {block.is_visible === false && (
        <div className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 border border-red-300 text-[10px] font-bold uppercase text-red-800">
          Oculto
        </div>
      )}
      {children}
    </div>
  );
}

function Block({ block, landing, programs, primary, secondary }) {
  const p = block.props || {};
  switch (block.type) {
    case "hero":
      return <HeroBlock p={p} primary={primary} />;
    case "features_grid":
      return <FeaturesBlock p={p} primary={primary} />;
    case "programs_showcase":
      return <ProgramsBlock p={p} programs={programs} primary={primary} />;
    case "testimonials":
      return <TestimonialsBlock p={p} primary={primary} secondary={secondary} />;
    case "plans_teaser":
      return <PlansTeaserBlock p={p} primary={primary} />;
    case "stats_row":
      return <StatsBlock p={p} primary={primary} />;
    case "cta_banner":
      return <CtaBannerBlock p={p} primary={primary} secondary={secondary} />;
    case "logos_row":
      return <LogosBlock p={p} />;
    case "rich_text":
      return <RichTextBlock p={p} />;
    case "image_full":
      return <ImageBlock p={p} />;
    default:
      return null;
  }
}

/* --------------------------------------------------------------- Blocks */

function HeroBlock({ p, primary }) {
  const centered = p.layout === "center";
  return (
    <section
      data-testid="partner-landing-hero"
      className="relative overflow-hidden"
      style={{
        backgroundImage: p.background_image_url ? `linear-gradient(rgba(15,23,42,0.6), rgba(15,23,42,0.7)), url(${p.background_image_url})` : undefined,
        backgroundSize: "cover", backgroundPosition: "center",
        backgroundColor: p.background_image_url ? undefined : "#F8FAFC",
      }}
    >
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 ${centered ? "text-center" : ""}`}>
        <div className={centered ? "max-w-3xl mx-auto" : "max-w-3xl"}>
          {p.eyebrow && (
            <span
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full border text-[11px] font-bold uppercase tracking-widest"
              style={{ color: primary, borderColor: `${primary}55`, backgroundColor: `${primary}0d` }}
            >
              {p.eyebrow}
            </span>
          )}
          <h1 className={`mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight ${p.background_image_url ? "text-white" : "text-slate-900"}`}>
            {p.title}
          </h1>
          {p.subtitle && (
            <p className={`mt-5 text-lg ${p.background_image_url ? "text-slate-100" : "text-slate-600"} max-w-2xl ${centered ? "mx-auto" : ""}`}>
              {p.subtitle}
            </p>
          )}
          {p.cta_text && (
            <div className={`mt-8 ${centered ? "" : ""}`}>
              <CtaLink to={p.cta_link || "/"} primary={primary}>
                {p.cta_text} <ArrowRight className="w-4 h-4" />
              </CtaLink>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function StatsBlock({ p, primary }) {
  if (!p.items?.length) return null;
  return (
    <section className="bg-white border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6">
        {p.items.map((it, i) => (
          <div key={i} className="text-center">
            <p className="text-4xl font-extrabold tabular-nums" style={{ color: primary }}>{it.value}</p>
            <p className="mt-1 text-xs uppercase tracking-widest text-slate-500 font-semibold">{it.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function FeaturesBlock({ p, primary }) {
  return (
    <Section heading={p.heading}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {(p.items || []).map((it, i) => (
          <div key={i} className="p-6 bg-slate-50 border border-slate-200 rounded-xl">
            <div className="w-10 h-10 rounded-md flex items-center justify-center mb-4" style={{ backgroundColor: `${primary}18`, border: `1px solid ${primary}44` }}>
              <ResolvedIcon name={it.icon} className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-slate-900">{it.title}</h3>
            <p className="mt-2 text-sm text-slate-600">{it.description}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function ProgramsBlock({ p, programs, primary }) {
  const slugs = p.program_slugs || [];
  return (
    <Section heading={p.heading} bg="slate">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {slugs.map((slug) => {
          const prog = programs[slug];
          if (!prog) {
            return (
              <div key={slug} className="p-5 bg-white border border-slate-200 rounded-xl border-dashed">
                <p className="text-xs text-slate-400 font-mono">{slug}</p>
                <p className="mt-2 text-sm text-slate-500 italic">Programa no disponible</p>
              </div>
            );
          }
          return (
            <Link
              key={slug}
              to={`/catalogo/${prog.slug}`}
              data-testid={`landing-program-${prog.code}`}
              className="p-5 bg-white border border-slate-200 rounded-xl hover:shadow-md transition-all flex flex-col"
              style={{ borderColor: undefined }}
            >
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 font-mono">{prog.code}</span>
                <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: primary }}>{prog.format}</span>
              </div>
              <h3 className="text-base font-bold text-slate-900 leading-snug">{prog.title}</h3>
              <div className="mt-auto pt-4 flex items-center justify-between text-xs">
                {prog.rating_avg > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    <span className="font-mono text-slate-700">{Number(prog.rating_avg).toFixed(1)}</span>
                  </span>
                )}
                {p.show_price && prog.price_mxn && (
                  <span className="font-mono font-bold text-slate-900">${Number(prog.price_mxn).toLocaleString("es-MX")}</span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </Section>
  );
}

function TestimonialsBlock({ p, primary, secondary }) {
  return (
    <Section heading={p.heading}>
      <div className="grid md:grid-cols-3 gap-5">
        {(p.items || []).map((t, i) => (
          <blockquote key={i} className="p-6 bg-slate-50 border border-slate-200 rounded-xl">
            <p className="text-sm text-slate-800 italic leading-relaxed">"{t.quote}"</p>
            <div className="mt-4 flex items-center gap-3">
              {t.avatar_url ? (
                <img src={t.avatar_url} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: i % 2 ? secondary : primary }}>
                  {(t.name || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div>
                <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                <p className="text-xs text-slate-500">{t.role}</p>
              </div>
            </div>
          </blockquote>
        ))}
      </div>
    </Section>
  );
}

function PlansTeaserBlock({ p, primary }) {
  return (
    <Section heading={p.heading} subtitle={p.subtitle} bg="slate">
      <div className="text-center">
        <Link
          to="/planes"
          data-testid="landing-plans-teaser-cta"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-white font-bold text-sm shadow-lg transition-transform hover:scale-[1.02]"
          style={{ backgroundColor: primary }}
        >
          Ver planes All-Access <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Section>
  );
}

function CtaBannerBlock({ p, primary, secondary }) {
  const gradient = `linear-gradient(135deg, ${primary}, ${secondary})`;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div
        className="relative overflow-hidden rounded-3xl p-10 md:p-14 text-center text-white"
        style={{ background: p.background_style === "solid" ? primary : gradient }}
      >
        <h2 className="text-3xl md:text-4xl font-extrabold">{p.title}</h2>
        {p.subtitle && <p className="mt-3 text-lg opacity-90 max-w-2xl mx-auto">{p.subtitle}</p>}
        {p.cta_text && (
          <div className="mt-7">
            <Link
              to={p.cta_link || "/"}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-md bg-white font-bold text-sm shadow-xl transition-transform hover:scale-[1.02]"
              style={{ color: primary }}
            >
              {p.cta_text} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

function LogosBlock({ p }) {
  return (
    <Section heading={p.heading} bg="slate">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {(p.items || []).map((it, i) => (
          <div key={i} className="h-20 flex items-center justify-center bg-white border border-slate-200 rounded-lg px-4">
            <span className="font-display font-semibold text-slate-500 tracking-wide text-sm text-center grayscale">{it.name}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function RichTextBlock({ p }) {
  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 prose prose-slate">
      <MarkdownContent className="!text-slate-800">{p.body_md}</MarkdownContent>
    </section>
  );
}

function ImageBlock({ p }) {
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {p.image_url ? (
        <figure>
          <img src={p.image_url} alt={p.alt || ""} className="w-full rounded-xl border border-slate-200" />
          {p.caption && <figcaption className="mt-2 text-center text-xs text-slate-500">{p.caption}</figcaption>}
        </figure>
      ) : (
        <div className="aspect-video flex items-center justify-center bg-slate-100 rounded-xl text-slate-400 text-sm">
          Imagen pendiente
        </div>
      )}
    </section>
  );
}

/* --------------------------------------------------------------- Helpers */

function Section({ heading, subtitle, children, bg }) {
  return (
    <section className={`${bg === "slate" ? "bg-slate-50" : "bg-white"} border-t border-slate-100`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {heading && (
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-display leading-tight">{heading}</h2>
            {subtitle && <p className="mt-3 text-slate-600">{subtitle}</p>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

function CtaLink({ to, primary, children }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md text-white font-bold text-sm shadow-lg transition-transform hover:scale-[1.02]"
      style={{ backgroundColor: primary }}
    >
      {children}
    </Link>
  );
}
